/**
 * Health insurance field sanitization + validation (primary/secondary tiers).
 */

export const INSURANCE_NAME_REGEX = /^[a-zA-Z\s'-]+$/;
export const SUBSCRIBER_ID_REGEX = /^[a-zA-Z0-9\s-]{4,20}$/;
export const INSURED_NAME_REGEX = /^[a-zA-Z\s'-]+$/;
export const GROUP_NO_REGEX = /^[a-zA-Z0-9\s-]{3,15}$/;

export const INSURANCE_FIELD_ERRORS = {
  insuranceName: 'Insurance name must contain letters, spaces, hyphens, or apostrophes',
  subscriberId: 'Subscriber ID must be 4-20 alphanumeric characters, hyphens, or spaces',
  insuredName: 'Name can only contain letters, spaces, hyphens, and apostrophes',
  groupNo: 'Group number must be 3-15 alphanumeric characters, hyphens, or spaces',
};

/** Map tier + field → error key used in AddPatientModal / PatientEditView */
export function insuranceErrorKey(tier, field) {
  return `${tier}${field.charAt(0).toUpperCase()}${field.slice(1)}`;
}

export function sanitizeInsuranceField(field, value) {
  const raw = value == null ? '' : String(value);
  switch (field) {
    case 'insuranceName':
      return raw.replace(/[^a-zA-Z\s'-]/g, '');
    case 'subscriberId':
      return raw.replace(/[^a-zA-Z0-9\s-]/g, '').slice(0, 20);
    case 'insuredName':
      return raw.replace(/[^a-zA-Z\s'-]/g, '');
    case 'groupNo':
      return raw.replace(/[^a-zA-Z0-9\s-]/g, '').slice(0, 15);
    default:
      return raw;
  }
}

export function getInsuranceFieldError(field, value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;

  switch (field) {
    case 'insuranceName':
      return INSURANCE_NAME_REGEX.test(trimmed) ? null : INSURANCE_FIELD_ERRORS.insuranceName;
    case 'subscriberId':
      return SUBSCRIBER_ID_REGEX.test(trimmed) ? null : INSURANCE_FIELD_ERRORS.subscriberId;
    case 'insuredName':
      return INSURED_NAME_REGEX.test(trimmed) ? null : INSURANCE_FIELD_ERRORS.insuredName;
    case 'groupNo':
      return GROUP_NO_REGEX.test(trimmed) ? null : INSURANCE_FIELD_ERRORS.groupNo;
    default:
      return null;
  }
}

/**
 * Validate primary + secondary insurance blocks.
 * @returns {Record<string, string>} error map keyed like primaryInsuranceName
 */
export function validateInsuranceData(insuranceData = {}) {
  const errors = {};
  ['primary', 'secondary'].forEach((tier) => {
    const data = insuranceData[tier] || {};
    const fields = {
      insuranceName: data.insuranceName || data.insurance_name || '',
      subscriberId: data.subscriberId || data.subscriber_id || '',
      insuredName: data.insuredName || data.insured_name || '',
      groupNo: data.groupNo || data.group_no || data.group_number || '',
    };
    Object.entries(fields).forEach(([field, value]) => {
      const message = getInsuranceFieldError(field, value);
      if (message) {
        errors[insuranceErrorKey(tier, field)] = message;
      }
    });
  });
  return errors;
}

export function isInsuranceErrorKey(key) {
  return /^(primary|secondary)(InsuranceName|SubscriberId|InsuredName|GroupNo)$/.test(String(key || ''));
}

export default {
  sanitizeInsuranceField,
  getInsuranceFieldError,
  validateInsuranceData,
  insuranceErrorKey,
  isInsuranceErrorKey,
};
